import { useNetworkTypeOptions } from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import React, { useEffect, useMemo, useState } from "react";
import { useOperationalStatus } from "../hooks";

interface NetworksFilterPanelProps {
  currentFilters?: {
    type?: string;
    status?: string;
  };
  onApplyFilters: (filters: any) => void;
  onResetFilters: () => void;
}

export const NetworksFilterPanel: React.FC<NetworksFilterPanelProps> = ({
  currentFilters = {},
  onApplyFilters,
  onResetFilters,
}) => {
  const [tempFilters, setTempFilters] = useState<{
    type: string;
    status: string;
  }>({
    type: currentFilters?.type || "all",
    status: currentFilters?.status || "all",
  });

  const { data: networkTypesData } = useNetworkTypeOptions();
  const { data: operationalStatusData } = useOperationalStatus();

  const networkTypeOptions = useMemo(() => {
    const options = [{ label: "All Types", value: "all" }];
    if (networkTypesData?.length > 0) {
      options.push(
        ...(networkTypesData?.map((item: any) => ({
          value: item.code,
          label: item.name,
        })) || [])
      );
    }
    return options;
  }, [networkTypesData]);

  const operationalStatusOptions = useMemo(() => {
    const options = [{ label: "All Status", value: "all" }];
    if (operationalStatusData?.length > 0) {
      options.push(
        ...operationalStatusData.map((status) => ({
          value: status.key,
          label: status.value,
        }))
      );
    }
    return options;
  }, [operationalStatusData]);

  const createFormFields = (): FormField[] => [
    {
      name: "type",
      type: "select",
      label: "Network Type",
      required: false,
      options: networkTypeOptions,
      placeholder: "Select network type",
      classes: FIELD_CLASSES,
    },
    {
      name: "status",
      type: "select",
      label: "Operating Status",
      required: false,
      options: operationalStatusOptions,
      placeholder: "Select status",
      classes: FIELD_CLASSES,
    },
  ];

  const form = FormService.createForm({
    fields: createFormFields(),
    defaultValues: tempFilters,
  });

  useEffect(() => {
    const newTempFilters = {
      type: currentFilters?.type || "all",
      status: currentFilters?.status || "all",
    };

    setTempFilters(newTempFilters);
    form.reset(newTempFilters);
  }, [currentFilters, form]);

  const handleSubmit = (data: any) => {
    const filtersToApply: any = {
      type: data.type && data.type !== "all" ? data.type : "",
      status: data.status && data.status !== "all" ? data.status : "",
    };

    onApplyFilters(filtersToApply);
  };

  // Handle reset
  const handleReset = () => {
    const defaultFilters = {
      type: "all",
      status: "all",
    };
    setTempFilters(defaultFilters);
    form.reset(defaultFilters);
    onResetFilters();
  };

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    setTempFilters((prev) => ({ ...prev, [fieldName]: value }));
  };

  // Custom footer with Apply and Reset buttons
  const renderCustomFooter = () => (
    <div className="col-span-1 md:col-span-2 flex justify-end gap-2">
      <Button type="button" variant="outline" onClick={handleReset}>
        Reset Filters
      </Button>
      <Button type="submit">Apply Filters</Button>
    </div>
  );

  return (
    <DynamicForm
      fields={createFormFields()}
      form={form}
      onSubmit={handleSubmit}
      onReset={handleReset}
      onFieldChange={handleFieldChange}
      config={{
        showSubmitButton: false, // We're using custom footer
        showResetButton: false, // We're using custom footer
        layout: "vertical",
        spacing: "medium",
      }}
      gridColumns={2}
      responsiveColumns={{ sm: 1, md: 2 }}
      className="space-y-6"
      renderCustomFooter={renderCustomFooter}
    />
  );
};
